import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { type AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KYCModalActionButtons } from "@features/KnowYourCustomerModal/components/KYCModalActionButtons/KYCModalActionButtons";
import { LoaderIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormPhoneInput } from "@components/inputs/form/FormPhoneInput/FormPhoneInput";
import { Notification } from "@components/Notification/Notification";
import {
  useApiErrorUtils,
  type IValidationError,
} from "@hooks/useApiErrorUtils";
import { useAuthenticationDataMutation } from "@services/knowYourCustomer/mutations";
import {
  useAuthenticationStatusQuery,
  useAuthenticationTypeRulesQuery,
} from "@services/knowYourCustomer/queries";
import {
  AuthenticationTypeSlug,
  type IAuthenticationDataRequest,
  type IAuthenticationDataResponse,
  type TAuthenticationFormType,
  type TAuthenticationType,
} from "@services/knowYourCustomer/types";
import { queryClient } from "@/queryClient";
import { useAuthenticationDataFormSchema } from "./useAuthenticationDataFormSchema";

interface IAuthenticationDataFormProps {
  authenticationType: TAuthenticationType;
  setStep: Dispatch<SetStateAction<TAuthenticationFormType>>;
  onAuthentication: () => void;
  onCancel: () => void;
  authPurpose: "identification" | "signDocument";
}

export const AuthenticationDataForm: React.FC<IAuthenticationDataFormProps> = ({
  authenticationType,
  setStep,
  onAuthentication,
  onCancel,
  authPurpose,
}) => {
  const [verificationData, setVerificationData] =
    useState<IAuthenticationDataResponse>();
  const [error, setError] = useState<IValidationError>();
  const { t } = useTranslation();
  const { data: authenticationRules } = useAuthenticationTypeRulesQuery(
    authPurpose === "identification"
      ? "authentication-types"
      : "document-signing-types",
    authenticationType,
  );

  const { mutateAsync: submitAuthenticationData, isPending } =
    useAuthenticationDataMutation(
      authPurpose === "identification"
        ? "authentications"
        : "document-signings",
      AuthenticationTypeSlug[authenticationType],
    );

  const { data: authenticationStatus, isError } = useAuthenticationStatusQuery(
    authPurpose === "identification" ? "authentications" : "document-signings",
    AuthenticationTypeSlug[authenticationType],
    verificationData?.sessionId,
  );

  const schema = useAuthenticationDataFormSchema(
    authenticationRules?.dataRequired,
  );

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<IAuthenticationDataRequest>({
    defaultValues: {
      personalCode: "",
      phoneNumber: "",
    },
    resolver: zodResolver(schema),
  });

  const { setApiErrors, getApiErrors } = useApiErrorUtils();

  const submitAuthenticationDataForm = useCallback(
    async (data?: IAuthenticationDataRequest) => {
      await submitAuthenticationData(data)
        .then(setVerificationData)
        .catch((errors: AxiosError) => {
          setApiErrors(errors, false);
          setError(
            getApiErrors(errors, false, true).find((err) => !err.fieldName),
          );
        });
    },
    [setVerificationData, submitAuthenticationData, setApiErrors, getApiErrors],
  );

  //TODO: refactor - all KYC works on hacky useEffects
  useEffect(() => {
    if (!authenticationRules) {
      return;
    }

    if (!authenticationRules.isDataRequired) {
      void submitAuthenticationData({})
        .then(setVerificationData)
        .catch((errors: AxiosError) => {
          setApiErrors(errors, false);
          setError(
            getApiErrors(errors, false, true).find((err) => !err.fieldName),
          );
        })
        .finally(
          () =>
            void queryClient.invalidateQueries({
              queryKey: [
                "knowYourCustomer",
                "authentication",
                authPurpose === "identification"
                  ? "authentication-types"
                  : "document-signing-types",
              ],
            }),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationRules]);

  useEffect(() => {
    if (verificationData?.redirectUrl) {
      window.location.href = verificationData.redirectUrl;
    }
  }, [verificationData]);

  useEffect(() => {
    const shouldToastError = authPurpose === "signDocument";

    if (isError) {
      if (shouldToastError) {
        toast.error(
          t(`KYC.modal.authentication.verification.toast.unknownError`),
        );
      }

      setStep("authenticationType");
      return;
    }

    if (!authenticationStatus || authenticationStatus === "waiting") {
      return;
    }

    if (authenticationStatus === "confirmed") {
      onAuthentication();
      return;
    }

    if (shouldToastError) {
      toast.error(
        t(
          `KYC.modal.authentication.verification.toast.${authenticationStatus}`,
        ),
      );
    }

    setStep("authenticationType");
  }, [
    authPurpose,
    authenticationStatus,
    setStep,
    t,
    isError,
    onAuthentication,
  ]);

  return (
    <>
      {verificationData ? (
        <div className="flex flex-col gap-6">
          {isPending || !verificationData.verificationCode ? (
            <LoaderIcon className="animate-spin self-center" />
          ) : (
            <>
              <p className="text-gray-900 p-0 text-lg font-semibold">
                {t("KYC.modal.authentication.verification.title")}
              </p>
              <span className="flex w-full items-center justify-center rounded-2xl bg-blue-light py-10 text-3xl font-medium">
                {verificationData.verificationCode}
              </span>
              <p>{t("KYC.modal.authentication.verification.description")}</p>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {isPending && !isDirty ? (
            <LoaderIcon className="animate-spin self-center" />
          ) : (
            <>
              <p className="text-gray-900 p-0 text-lg font-semibold">
                {t("KYC.modal.authentication.data.title")}
              </p>
              <form
                onSubmit={handleSubmit(submitAuthenticationDataForm)}
                className="flex flex-col gap-3"
              >
                {authenticationRules?.dataRequired?.phoneNumberRequired && (
                  <FormPhoneInput
                    control={control}
                    name="phoneNumber"
                    label={t(
                      "KYC.modal.authentication.data.form.fields.phoneNumber.label",
                    )}
                  />
                )}
                {authenticationRules?.dataRequired?.personalCodeRequired && (
                  <FormInput
                    control={control}
                    name="personalCode"
                    label={t(
                      "KYC.modal.authentication.data.form.fields.personalCode.label",
                    )}
                  />
                )}
                {error && <Notification description={error.text} color="red" />}
              </form>
            </>
          )}
          <KYCModalActionButtons
            onSubmit={handleSubmit(submitAuthenticationDataForm)}
            onCancel={onCancel}
            cancelButtonText={t("KYC.modal.buttons.back")}
            isLoading={isPending}
          />
        </div>
      )}
    </>
  );
};
